import { ReactNode, useEffect, useRef, useState } from "react";

import { useFolderScope } from "../ChooseEntityDialog/folder-scope-context-provider";
import { Button } from "../ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../ui/dialog";
import { Input } from "../ui/input";
import UnifiedRegistry from "../UnifiedRegistry";
import { UnifiedRegistryProvider } from "../UnifiedRegistry/unified-registry-context";
import { getEntityName, getItemColor } from "../UnifiedRegistry/utils";

import { EntityType, getEntityNumericId } from "@/queries";
import { displayErrorToast } from "@/utils/toast";
import { EntityIcon } from "@/components/ui/icons";
export interface SaveEntityToLocationProps {
  type: EntityType;
  onSave: (name: string, folderId: number | null) => Promise<void>;
  currentFolderId?: number | null;
  defaultName?: string;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  disabled?: boolean;
  title?: string;
  description?: string;
  saveButtonText?: string;
  footerContent?: ReactNode;
  isSaveDisabled?: boolean;
  children?: ReactNode;
}

export const useSaveEntityToLocation = () => {
  const [open, setOpen] = useState(false);

  const openDialog = () => setOpen(true);
  const closeDialog = () => setOpen(false);

  return {
    open,
    openDialog,
    closeDialog,
    setOpen,
  };
};

export const SaveEntityToLocationContent = ({
  open,
  onOpenChange,
  onSave,
  type,
  currentFolderId: propCurrentFolderId,
  defaultName = "",
  title,
  description,
  saveButtonText = "Save",
  footerContent,
  isSaveDisabled = false,
  children,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  children?: ReactNode;
} & Pick<
  SaveEntityToLocationProps,
  | "type"
  | "onSave"
  | "currentFolderId"
  | "defaultName"
  | "title"
  | "description"
  | "saveButtonText"
  | "footerContent"
  | "isSaveDisabled"
>) => {
  const folderScope = useFolderScope();
  const initialFolderId = propCurrentFolderId ?? folderScope;
  const [selectedFolderId, setSelectedFolderId] = useState<number | null>(initialFolderId);
  const [name, setName] = useState(defaultName);
  const [isSaving, setIsSaving] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) {
      setSelectedFolderId(initialFolderId);
      setName(defaultName);
      setIsSaving(false);
      // Focus the input after a short delay to ensure the dialog is fully rendered
      setTimeout(() => {
        inputRef.current?.focus();
      }, 0);
    }
  }, [open, initialFolderId, defaultName]);

  const handleSave = async () => {
    if (!name.trim() || isSaving || isSaveDisabled) {
      return;
    }

    setIsSaving(true);
    try {
      await onSave(name.trim(), selectedFolderId);
      onOpenChange(false);
    } catch (error) {
      displayErrorToast(error);
    } finally {
      setIsSaving(false);
    }
  };

  const displayTitle = title || `Save ${getEntityName(type)}`;
  const displayDescription =
    description || `Choose a location and name for your ${getEntityName(type).toLowerCase()}.`;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {children && <DialogTrigger asChild>{children}</DialogTrigger>}

      <DialogContent className="flex h-full max-h-[720px] max-w-4xl flex-col gap-0 p-0">
        <DialogHeader className="border-b border-gray-200 px-6 py-4">
          <DialogTitle className="flex items-center gap-3 text-lg font-semibold text-gray-900">
            <div
              className={`flex h-10 w-full max-w-[40px] flex-shrink-0 items-center justify-center rounded-full ${getItemColor(
                type,
              )}`}
            >
              <EntityIcon type={type} size="md" />
            </div>
            <span className="min-w-0 flex-1">{displayTitle}</span>
          </DialogTitle>
          <DialogDescription className="text-sm text-gray-600">
            {displayDescription}
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-hidden border-b border-gray-200">
          <UnifiedRegistry
            currentFolderId={selectedFolderId}
            onItemClick={item => {
              if (item.type === EntityType.FOLDER) {
                setSelectedFolderId(getEntityNumericId(item.id) ?? null);
              }
            }}
          />
        </div>

        <DialogFooter className="px-6 py-4">
          <div className="w-full space-y-3">
            {footerContent}
            <div className="flex w-full items-center gap-3">
              <Input
                ref={inputRef}
                placeholder={`${getEntityName(type)} name`}
                value={name}
                onChange={e => setName(e.target.value)}
                onKeyDown={e => {
                  if (e.key === "Enter" && name.trim() && !isSaving && !isSaveDisabled) {
                    void handleSave();
                  }
                }}
                className="flex-1"
              />
              <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isSaving}>
                Cancel
              </Button>
              <Button
                onClick={handleSave}
                disabled={!name.trim() || isSaving || isSaveDisabled}
                isLoading={isSaving}
              >
                {saveButtonText}
              </Button>
            </div>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const SaveEntityToLocationProvider = ({
  children,
}: {
  children: ReactNode;
  type: EntityType;
}) => (
  <UnifiedRegistryProvider
    options={{
      whitelist: [EntityType.FOLDER],
      disableSelection: true,
      createInNewTab: true,
      disabled: () => false,
      modal: true,
    }}
  >
    {children}
  </UnifiedRegistryProvider>
);

export const SaveEntityToLocationControlled = ({
  open: controlledOpen,
  onOpenChange: controlledOnOpenChange,
  ...props
}: SaveEntityToLocationProps) => {
  if (controlledOpen === undefined || !controlledOnOpenChange) {
    throw new Error("SaveEntityToLocationControlled requires both open and onOpenChange props");
  }

  return (
    <SaveEntityToLocationProvider type={props.type}>
      <SaveEntityToLocationContent
        open={controlledOpen}
        onOpenChange={controlledOnOpenChange}
        type={props.type}
        onSave={props.onSave}
        currentFolderId={props.currentFolderId}
        defaultName={props.defaultName}
        title={props.title}
        description={props.description}
        saveButtonText={props.saveButtonText}
        footerContent={props.footerContent}
        isSaveDisabled={props.isSaveDisabled}
      />
    </SaveEntityToLocationProvider>
  );
};

const SaveEntityToLocation = ({
  type,
  onSave,
  currentFolderId,
  defaultName = "",
  open: controlledOpen,
  onOpenChange: controlledOnOpenChange,
  disabled = false,
  title,
  description,
  saveButtonText = "Save",
  footerContent,
  isSaveDisabled = false,
  children,
}: SaveEntityToLocationProps) => {
  const { open: internalOpen, setOpen: setInternalOpen } = useSaveEntityToLocation();

  const open = controlledOpen !== undefined ? controlledOpen : internalOpen;
  const onOpenChange = controlledOnOpenChange || setInternalOpen;

  if (disabled) {
    return <>{children}</>;
  }

  return (
    <SaveEntityToLocationProvider type={type}>
      <SaveEntityToLocationContent
        open={open}
        onOpenChange={onOpenChange}
        type={type}
        onSave={onSave}
        currentFolderId={currentFolderId}
        defaultName={defaultName}
        title={title}
        description={description}
        saveButtonText={saveButtonText}
        footerContent={footerContent}
        isSaveDisabled={isSaveDisabled}
      >
        {children}
      </SaveEntityToLocationContent>
    </SaveEntityToLocationProvider>
  );
};

export default SaveEntityToLocation;
